import {observable, action, makeObservable} from 'mobx';
import get from 'lodash/get';
import * as Sentry from '@sentry/react';

//helpers
import ResetPasswordForm, {
  createLocalisedForm as createResetPasswordLocalisedForm
} from '../stores/forms/reset-password-form';

import {posthogCapture} from '../utils/posthog-utils';
import {posthogEvents} from '../posthog-events';

import {emailValidationRegex} from '../constants';
import phone from 'phone';
import {DOMAIN_LOGIN_OPTIONS} from 'enums';
import {kebabCase} from 'lodash';

class ResetPasswordPage {
  @observable isRequestedReset = false;
  @observable isInvalidLink = false;
  @observable emailIsNotSent = false;
  @observable loading = false;
  @observable passwordsMatch = true;
  @observable isValidNewPassword = true;
  @observable isInputFocused = false;

  form = ResetPasswordForm;
  translations = {};

  @action
  setRequestReset = val => (this.isRequestedReset = val);

  @action
  setNotSendEmail = val => (this.emailIsNotSent = val);

  @action
  setLoading = val => (this.loading = val);

  @action
  setIsInputFocused = isInputFocused => {
    this.isInputFocused = isInputFocused;
  };

  @action validateEmail = forgotPasswordMutation => {
    const {email} = this.form.values();
    if (emailValidationRegex.test(email)) {
      this.submitForgotPasswordForm(forgotPasswordMutation);
    }
  };

  @action validatePhoneNumber = forgotPasswordMutation => {
    const {phoneNumber} = this.form.values();
    const parsedPhoneNumber = phone(phoneNumber);
    if (parsedPhoneNumber.isValid) {
      this.submitForgotPasswordForm(forgotPasswordMutation, true);
    }
  };

  @action validatePassword = (resetPasswordMutation, router, store, views, mode) => {
    const {newPassword, newPasswordConfirm} = this.form.values();
    this.isValidNewPassword = this.isPasswordValid(newPassword);
    if (this.isValidNewPassword) {
      this.passwordsMatch = newPassword === newPasswordConfirm;
    }
    if (this.passwordsMatch && this.isValidNewPassword) {
      this.submitResetPasswordForm(resetPasswordMutation, router, store, views, mode);
    }
  };

  @action
  submitForgotPasswordForm = (forgotPasswordMutation, isPhone) => {
    const {email, phoneNumber} = this.form.values();
    this.setLoading(true);
    forgotPasswordMutation({email, phoneNumber})
      .then(response => {
        if (!get(response, 'data.forgotPassword.result')) {
          return Promise.reject();
        }
        if (isPhone) {
          this.store.router.goTo(this.store.app.views.resetPasswordPhone, undefined, {phoneNumber});
        } else {
          this.isRequestedReset = true;
          this.emailIsNotSent = false;
        }
        this.setLoading(false);
      })
      .catch(this.handleForgotPasswordError);
  };

  @action
  submitResetPasswordForm = async (resetPasswordMutation, router, store, views, mode) => {
    let phoneNumber = this.store.router.queryParams.phoneNumber;
    if (phoneNumber) {
      phoneNumber = phoneNumber.replace('%2B', '+');
    }
    const {resetCode, newPassword} = this.form.values();
    let {token} = router.queryParams;
    if (!token) {
      token = resetCode;
    }
    try {
      const successKey = mode === 'setPassword' ? 'setPassword' : 'resetPassword';

      const result = await resetPasswordMutation({token, phoneNumber, password: newPassword});
      if (!result.data[successKey]) throw new Error(result.errors?.[0].message);
      posthogCapture(posthogEvents.resetPasswordSuccessful);
      this.notification.success(this.translations.passwordReset);
      this.form.clear();

      router.goTo(views.signIn, {
        loginType: kebabCase(phoneNumber ? DOMAIN_LOGIN_OPTIONS.PHONE_NUMBER : DOMAIN_LOGIN_OPTIONS.EMAIL)
      });
    } catch (error) {
      Sentry.captureException(error);
      this.handleResetPasswordError(error);
    }
  };

  @action
  resetPasswordLinkNotValid = () => {
    this.isInvalidLink = true;
  };

  @action
  handleForgotPasswordError = () => {
    this.notification.error(this.translations.canNotSentPasswordResetLink);
    this.emailIsNotSent = true;
    this.setLoading(false);
  };

  @action
  handleResetPasswordError = error => {
    let errorMessage = this.translations.canNotResetPassword;
    if (error.message === 'Invalid reset password pincode') {
      errorMessage = this.translations.invalidResetCode;
    }
    this.notification.error(errorMessage);
  };

  @action
  reset = () => {
    this.form.clear();
    this.setLoading(false);
    this.isRequestedReset = false;
    this.emailIsNotSent = true;
  };

  setStore = store => {
    this.store = store;
  };

  setNotification = notification => {
    this.notification = notification;
  };

  constructor() {
    makeObservable(this);
  }

  setTranslations(translations) {
    this.translations = translations;
    this.form = createResetPasswordLocalisedForm(translations);
  }
  isPasswordValid(password) {
    const hasMinCharacters = password.length >= 8;
    const hasLowercaseChar = /[a-z]/.test(password);
    const hasUppercaseChar = /[A-Z]/.test(password);
    const containsNumber = /\d/.test(password);
    const containsNonAlphaChar = /[^a-zA-Z0-9\s:]/.test(password);
    const isPasswordValid =
      hasMinCharacters && hasLowercaseChar && hasUppercaseChar && containsNumber && containsNonAlphaChar;
    if (isPasswordValid) {
      return true;
    } else {
      this.passwordsMatch = true;
      return false;
    }
  }
}
export default ResetPasswordPage;
